<script setup lang="ts">
    import type { PageSettingsStoryblok } from '~/.storyblok/types/PageSettingsStoryblok';
    import { COOKIE_NAME_CONFIRMATION_GATE_ACCEPTED } from '~/types/cookieNames';
    import { CONFIRMATION_GATE_COOKIE_OPTIONS } from '~/types/cookieOptions';
    import { isConfirmationGateSelfTarget } from './isConfirmationGateSelfTarget';
    import log from '~/utils/log';

    const props = defineProps<{
        country: string;
        settings?: PageSettingsStoryblok;
    }>();

    const gateAccepted = useCookie<boolean>(
        `${COOKIE_NAME_CONFIRMATION_GATE_ACCEPTED}-${props.country}`,
        CONFIRMATION_GATE_COOKIE_OPTIONS
    );

    const isConfirmationGated = computed(() => Boolean(props.settings?.hasGate) && gateAccepted.value !== true);
    const { isInEditor } = useDraftMode();
    const route = useRoute();
    const baseUrl = useRuntimeConfig().public.baseUrl;
    const sectionRootUrl = useCurrentSectionRootUrl();
    const gateDenied = ref(false);
    const content = ref<HTMLElement>();

    const denyRedirectUrl = computed(() => {
        try {
            const link = props.settings?.gateDeniedRedirectTarget;
            const hasTarget = Boolean(link?.id || link?.url || link?.cached_url);
            const target = (hasTarget ? useStoryblokUrl(link, route) : '') || sectionRootUrl.value;
            const currentUrl = new URL(route.path, baseUrl);
            const targetUrl = new URL(target, currentUrl);

            if (targetUrl.protocol !== 'https:' && targetUrl.protocol !== 'http:') {
                throw new Error(`Unsupported redirect protocol: ${targetUrl.protocol}`);
            }

            return targetUrl.origin === currentUrl.origin && !targetUrl.pathname.startsWith('//')
                ? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
                : targetUrl.href;
        } catch (error) {
            log.warn('[confirmationGate:deny] invalid redirect target', error);
            return null;
        }
    });
    const isSelfTarget = computed(() =>
        denyRedirectUrl.value ? isConfirmationGateSelfTarget(denyRedirectUrl.value, route.path, baseUrl) : false
    );

    watch([denyRedirectUrl, () => route.path], () => {
        gateDenied.value = false;
    });

    async function handleDeny() {
        if (!denyRedirectUrl.value || isSelfTarget.value) {
            gateDenied.value = true;
            return;
        }

        const redirectTarget = denyRedirectUrl.value;
        log.debug(`[confirmationGate:deny] redirect to ${redirectTarget}`);

        try {
            await navigateTo(redirectTarget, {
                external: /^https?:\/\//i.test(redirectTarget)
            });
        } catch (error) {
            log.warn('[confirmationGate:deny] redirect failed', error);
            gateDenied.value = true;
        }
    }

    async function handleConfirm() {
        gateAccepted.value = true;
        await nextTick();
        content.value?.focus();
    }
</script>

<template>
    <SharedPageContent v-if="isInEditor && settings?.hasGate && isSelfTarget">
        <Note type="error">
            {{ $t('confirmationGate.selfTargetEditorError') }}
        </Note>
    </SharedPageContent>
    <ConfirmationGate
        v-if="settings && isConfirmationGated"
        :blok="settings"
        :denied="gateDenied"
        @confirm="handleConfirm"
        @deny="handleDeny"
    />
    <div
        v-else-if="settings?.hasGate"
        ref="content"
        tabindex="-1"
    >
        <slot />
    </div>
    <slot v-else />
</template>
